import type { UserState } from "@/store/user/state";
import type { ScoutOtelToken } from "@/utils/jwt";
import { isValid } from "@/utils/jwt";
import type { AppGroup, IngestKey } from "@/models/manager";

/**
 * Retrieve the user JWT token.
 * @param state - Vuex state for the user module.
 * @returns JWT token for the user if it has been assigned or undefined otherwise.
 */
export function getToken(state: UserState): string | undefined {
  return state.token;
}

/**
 * Determine whether the user is still in a valid logged in state based on the
 * user JWT being set and unexpired.
 * @param state - Vuex state for the user module.
 * @returns boolean whether the user is currently logged in.
 */
export function isLoggedIn(state: UserState): boolean {
  // TODO: Implement JWT unexpired and exists checking.
  return !!(state.token && isValid(state.parsedToken));
}

/**
 * Determine if the user is an admin in their account.
 * This is for display purposes only as the backend
 * is required to to validate the token to ensure the claims have not
 * been set in bad faith.
 * @param state - Vuex state for the user module.
 * @returns whether the user is an admin. Will be false if the token
 *   was invalid or has not been set.
 */
export function isAdmin(state: UserState): boolean {
  return state.parsedToken?.rbac === "admin";
}

/**
 * Retrieve the parsed JWT token.
 * @param state - Vuex state for the user module.
 * @returns Parsed ScoutOtel JWT token for the user if it has been assigned or undefined otherwise.
 */
export function getParsedToken(state: UserState): ScoutOtelToken | undefined {
  return state.parsedToken;
}

/**
 * Retrieve the user Id.
 * @param state - Vuex state for the user module.
 * @returns UUID of user Id.
 */
export function getId(state: UserState): string | undefined {
  return state.userId;
}

/**
 * Retrieve the user account Id.
 * @param state - Vuex state for the user module.
 * @returns UUID of account Id.
 */
export function getAccountId(state: UserState): string | undefined {
  return state.accountId;
}
/**
 * Retrieve the user email.
 * @param state - Vuex state for the user module.
 * @returns email of user
 */
export function getEmail(state: UserState): string | undefined {
  return state.email;
}

/**
 * Retrieve the user appgroup id.
 * @param state - Vuex state for the user module.
 * @returns appgroup Id of current appgroup
 */
export function getAppgroupId(state: UserState): string | undefined {
  return state.appGroupId;
}

/**
 * Retrieve the user appgroup name.
 * @param state - Vuex state for the user module.
 * @returns appgroup name of current appgroup
 */
export function getAppgroupName(state: UserState): string | undefined {
  return state.appGroupName;
}

/**
 * Retrieve the user first name.
 * @param state - Vuex state for the user module.
 * @returns first name of current user.
 */
export function getFirstName(state: UserState): string | undefined {
  return state.firstName;
}
/**
 * Retrieve the user last name.
 * @param state - Vuex state for the user module.
 * @returns last name of current user.
 */
export function getLastName(state: UserState): string | undefined {
  return state.lastName;
}
/**
 * Retrieve the accessible service groups for the user
 * @param state - Vuex state for the user module.
 * @returns service groups for current user.
 */
export function getServiceGroups(state: UserState): AppGroup[] | undefined {
  return state.serviceGroups;
}
/**
 * Retrieve the billingThreshold for the account.
 * @param state - Vuex state for the user module.
 * @returns Billing threshold.
 */
export function getBillingThreshold(state: UserState): number | undefined {
  return state.account.billingThreshold || undefined;
}
/**
 * Retrieve the packageId for the account.
 * @param state - Vuex state for the user module.
 * @returns Package ID.
 */
export function getPackageId(state: UserState): string | undefined {
  return state.account.packageId || state.packageId || undefined;
}
/**
 * Retrieve the deactivate date for the account.
 * @param state - Vuex state for the user module.
 * @returns Deactivation date.
 */
export function getDeactivatedAt(state: UserState): string | undefined {
  return state.account.deactivatedAt || undefined;
}

/**
 * Retrieve the Account's inTrial value.
 * @param state - Vuex state for the user module.
 * @returns InTrial's boolean value.
 */
export function getAccountInTrial(state: UserState): boolean | undefined {
  return state.account.inTrial;
}

/**
 * Retrieve the stripe setup intent client secret
 * @param state - Vuex state for the user module.
 * @returns Stripe setup intent client secret.
 */
export function getStripeSetupIntentClientSecret(
  state: UserState
): string | undefined {
  return state.stripeSetupIntentClientSecret || undefined;
}

/**
 * Retrieve the serviceGroup to IngestKey mapping value stored in Vuex.
 * @param state - Vuex state for the User module.
 * @returns - The value of the serviceGroupIngestKeyMapping state.
 */
export function getServiceGroupIngestKeyMapping(
  state: UserState
): Record<string, IngestKey[]> | undefined {
  return state.serviceGroupIngestKeyMapping;
}

/**
 * Retrieve the ID of the selected serviceGroup using the IngestKeyPicker component.
 * @param state - Vuex state for the User module.
 * @returns - The value of the serviceGroupIdSelected state.
 */
export function getServiceGroupIdSelected(
  state: UserState
): string | undefined {
  return state.serviceGroupIdSelected;
}

/**
 * Retrieve the ID of the selected ingestKey using the IngestKeyPicker component.
 * @param state - Vuex state for the User module.
 * @returns - The value of the ingestKeyIdSelected state.
 */
export function getIngestKeyIdSelected(state: UserState): string | undefined {
  return state.ingestKeyIdSelected;
}
